import { useMemo, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { resolveDeclineCopy } from 'src/utils/resolveBillingError';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { resolveSuggestedTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import {
  ePlanFrequency,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import {
  LEAD_AGENT_TIER_ORDER,
  buildUpgradeRenewalBreakdown,
  formatLeadAgentRenewalPrice,
  isReactivateChargeProrated,
  parsePlanFrequency,
  renewalPeriodKey,
  resolveDeferredFirstChargeAt,
  resolveReactivateCopyVariant,
} from '../acceptanceModals.functions';
import { useReactivateLeadAgentModal } from './ReactivateLeadAgentModal.hook';
import type { tReactivateLeadAgentModalProps } from './ReactivateLeadAgentModal.types';
import CheckoutCopyGate from '../CheckoutCopyGate.component';

const NS = 'common/leadAgentReactivateModal';
const LOCALIZED_BLOCK_CODES = new Set([
  'already_active',
  'subscription_past_due',
  'lead_agent_item_presence_unknown',
]);

/**
 * Lead Agent reactivation modal. Reactivation charges IMMEDIATELY through the
 * A11 endpoints (preview → reactivate), so this is a separate component from
 * the deferred-PATCH ConfirmTierModal, not a mode flag on it
 * (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
 *
 * View machine (in the hook): confirm → processing → success | error | blocked.
 * The success copy says the charge is PROCESSING and the bot re-enables once
 * the payment confirms (A11 writes no feature_usages — the webhook provisions),
 * so it never claims the Lead Agent is active immediately.
 */
export default function ReactivateLeadAgentModal(
  props: tReactivateLeadAgentModalProps,
) {
  const { onClose, onReactivated } = props;
  const { t, ready: copyReady } = useTranslation(NS);
  // The billing-error copy (decline map) lives in its own namespace (RET-1372);
  // resolveDeclineCopy requires a `t` bound to it (RET-1972).
  const { t: tBilling } = useTranslation('settings/SubscriptionManager');
  const { addAlert } = useToastContext();
  const { acceptance } = useLeadAgentAcceptanceContext();
  const { subscription } = useSubscriptionContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  // The acceptance entity reaches only the Owner and is null without one, so
  // the workspace being charged comes from the workspace context.
  const workspaceId = currentWorkspaceId ?? '';
  const suggestedTier = resolveSuggestedTier(acceptance?.suggestedTier);

  const {
    view,
    preview,
    projection,
    selectedTier,
    confirmDisabled,
    selectTier,
    confirm,
    retry,
    noticeCode,
  } = useReactivateLeadAgentModal({
    workspaceId,
    suggestedTier,
    nextPaymentAt: resolveDeferredFirstChargeAt(
      acceptance,
      subscription?.frequency ?? null,
    ),
    origin: acceptance?.origin ?? null,
  });

  const [showAllTiers, setShowAllTiers] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Inline terms-consent error: the submit stays enabled (see `submitDisabled`),
  // and clicking it without accepting flips this true to surface a red checkbox +
  // helper line — not a toast, not a disabled button (Fanny, RET-1431).
  const [showTermsError, setShowTermsError] = useState(false);

  const isProcessing = view.status === 'processing';

  const amountLabel = useMemo(() => {
    if (preview.status !== 'ready') return null;
    return formatPrice({
      price: preview.data.amountCents,
      currency: preview.data.currency,
    });
  }, [preview]);

  // ---- Informational renewal projection (RET-1417), all fail-open ----
  // Every value below is null/false unless the projection RESOLVED for the
  // CURRENTLY SELECTED tier; none of them feed `confirmDisabled`, so a
  // projection failure never blocks the charge (it only hides the section).
  //
  // Tier-coherence guard: `selectedTier` flips synchronously on a tier switch
  // while the previous tier's projection stays `ready` until its abort+refetch
  // resolves. Reading that stale data would show tier A's price/total next to
  // tier B's radio. Gate every projection-derived value on the projection's own
  // `leadAgentLine.tier` matching the selected tier.
  const readyProjectionData = useMemo(
    () =>
      projection.status === 'ready' &&
      projection.data.leadAgentLine.tier === selectedTier
        ? projection.data
        : null,
    [projection, selectedTier],
  );

  // Period suffix + total word derive from the projection's OWN frequency (the
  // same source as the amounts), validated; falls back to the subscription only
  // when the projection lacks a usable value. Avoids an annual amount rendering
  // a "/mes" suffix when the subscription summary hasn't loaded yet.
  const displayFrequency =
    parsePlanFrequency(readyProjectionData?.frequency) ??
    subscription?.frequency ??
    ePlanFrequency.MONTHLY;

  /*
   * The DEDUPING builder. This modal used `buildRenewalBreakdown`, and for its
   * population that undercounts nothing and OVERcounts the add-on by a full
   * month of the tier.
   *
   * Why here specifically: the projection composes the recurring lines from
   * purchased_products and then ADDS the requested tier, without deduping. When
   * the Lead Agent lapses, access is revoked but the purchased_products row is
   * NOT removed — the removal path only fires when Stripe drops the item from
   * the subscription. So a "vencido" workspace can still list its old tier as a
   * recurring line while this modal asks for the tier it wants back, and the
   * backend total counts both. Verified against production 2026-09-01: 7 lapsed
   * workspaces still carry the row (3 on a live subscription, so reachable
   * here), at 2,119.32 MXN for Starter and 7,234.92 for Growth.
   *
   * The dedupe degrades to a no-op when no such line arrives, so the workspaces
   * whose row WAS cleaned are unaffected.
   */
  const breakdown = useMemo(
    () =>
      readyProjectionData
        ? buildUpgradeRenewalBreakdown(readyProjectionData)
        : null,
    [readyProjectionData],
  );

  // Option A: the card price comes from the projection's Lead Agent line, so
  // only the SELECTED tier has a price (the projection carries one tier).
  const selectedTierPrice = readyProjectionData
    ? formatLeadAgentRenewalPrice(readyProjectionData, displayFrequency)
    : null;

  // Translated period word for the renewal-total label ("Total {anual}…").
  const periodKey = renewalPeriodKey(displayFrequency);
  const periodLabel = periodKey ? t(`nextRenewal.period.${periodKey}`) : '';

  // Manual collection, non-monthly and trialing subscriptions remain on the
  // legacy route even if their acceptance row is before renewal (KTD4).
  const isSendInvoice =
    preview.status === 'ready' &&
    preview.data.collectionMethod === 'send_invoice';
  // The preview is the current charge route and wins once loaded. Acceptance is
  // only a rolling-deploy fallback for older preview payloads without `phase`.
  const routePhase =
    preview.status === 'ready' && preview.data.phase
      ? preview.data.phase
      : acceptance?.phase;
  const isBeforeRenewal =
    routePhase === 'before_renewal' &&
    displayFrequency === ePlanFrequency.MONTHLY &&
    !isSendInvoice &&
    subscription?.status !== eSubscriptionStatus.TRIAL;

  // Conditional explanatory copy variant + proration note (both informational).
  const projectionCopyVariant = readyProjectionData
    ? resolveReactivateCopyVariant(readyProjectionData)
    : null;
  // The backend phase owns the route. A projection's `firstCharge.kind` is
  // amount metadata and must not decide whether the plan already renewed.
  const copyVariant = isBeforeRenewal
    ? 'before_renewal'
    : readyProjectionData
      ? routePhase === 'after_renewal' || routePhase === 'trial_ended'
        ? 'full'
        : projectionCopyVariant
      : null;
  // Proration note only when today's charge differs from the full LA renewal
  // price AND both amounts are in the same currency (the cents comparison is
  // meaningless across currencies).
  const isProrated =
    !isBeforeRenewal &&
    preview.status === 'ready' &&
    readyProjectionData !== null &&
    preview.data.currency === readyProjectionData.currency &&
    isReactivateChargeProrated(preview.data.amountCents, readyProjectionData);

  const localizedBlockedCopy =
    view.status === 'blocked' &&
    view.code &&
    LOCALIZED_BLOCK_CODES.has(view.code)
      ? t(`blocked.codes.${view.code}`)
      : t('blocked.body');

  // Every guard that blocks a user action must surface feedback — a bare return
  // is an invisible dead button
  // (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
  function handleClose() {
    if (isProcessing) {
      addAlert({ type: eAlertType.INFO, message: t('processing.wait') });
      return;
    }
    onClose();
  }

  function handleSelectTier(tier: tLeadAgentSelectableTier) {
    if (tier === selectedTier || isProcessing) return;
    setAcceptedTerms(false);
    setShowTermsError(false);
    selectTier(tier);
  }

  function handleConfirm() {
    // Loading/in-flight cases keep the button disabled, so this guard is a safety
    // net rather than a UI-reachable path.
    if (confirmDisabled) return;
    // Terms consent is enforced here (button stays enabled): surface the inline
    // error instead of charging.
    if (!acceptedTerms) {
      setShowTermsError(true);
      return;
    }
    if (view.status === 'error') {
      retry();
      return;
    }
    confirm();
  }

  function handleSuccessDismiss() {
    if (view.status === 'success') {
      onReactivated?.({ tier: view.result.targetTier, result: view.result });
    }
    onClose();
  }

  // Tiers in display order; the suggested one first.
  const orderedTiers: tLeadAgentSelectableTier[] = [
    suggestedTier,
    ...LEAD_AGENT_TIER_ORDER.filter((tier) => tier !== suggestedTier),
  ];

  const submitLoading = isProcessing;
  // `confirmDisabled` (preview not ready / in flight) gates BOTH the submit button
  // and the terms checkbox — terms consent is never part of it (Fanny, RET-1431).
  // The inline terms error shows ONLY while the checkbox is actionable, so it never
  // lingers beneath a disabled checkbox — e.g. after the owner clicks submit
  // unchecked and then hits the preview retry.
  const showTermsErrorVisible = showTermsError && !confirmDisabled;

  // Sin la copy de este modal no hay nada que leer: `t()` devolvería
  // llaves crudas junto a montos correctos. Ver CheckoutCopyGate.
  if (!copyReady) {
    return (
      <CheckoutCopyGate
        className="LeadAgentReactivateModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={handleClose}
      className="LeadAgentReactivateModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
    >
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold">{t('title')}</h3>
        <XMarkIcon
          onClick={handleClose}
          className="size-6 cursor-pointer text-neutral-500"
          data-testid="reactivate-modal-close"
        />
      </div>

      {/* ---- SUCCESS ---- */}
      {view.status === 'success' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="reactivate-modal-success"
        >
          {/* RET-1649: a send_invoice reactivation returns status 'pending' — an
              invoice was emailed, NOT a payment received. Swap the "we got your
              payment" copy for invoice framing so the terminal screen is honest. */}
          <p className="text-base font-medium">
            {t(
              isBeforeRenewal
                ? 'success.beforeRenewalTitle'
                : view.result.status === 'pending' && isSendInvoice
                  ? 'success.invoiceSentTitle'
                  : view.result.status === 'pending'
                    ? 'success.openInvoiceTitle'
                    : 'success.title',
            )}
          </p>
          <p className="text-sm text-neutral-600">
            {t(
              isBeforeRenewal
                ? 'success.beforeRenewalBody'
                : view.result.status === 'pending' && isSendInvoice
                  ? 'success.invoiceSentBody'
                  : view.result.status === 'pending'
                    ? 'success.openInvoiceBody'
                    : 'success.processing',
            )}
          </p>
          {view.result.invoiceId ? (
            <p
              className="text-xs text-neutral-500"
              data-testid="reactivate-modal-success-invoice"
            >
              {t('success.invoice', { invoiceId: view.result.invoiceId })}
            </p>
          ) : null}
          <Button
            color="primary"
            size="sm"
            className="rounded self-end"
            onClick={handleSuccessDismiss}
            data-testid="reactivate-modal-success-dismiss"
          >
            {t('success.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- BLOCKED (4xx business rule, NOT retryable) ---- */}
      {view.status === 'blocked' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="reactivate-modal-blocked"
        >
          <p className="text-base font-medium text-error">
            {t('blocked.title')}
          </p>
          <p className="text-sm text-neutral-600">{localizedBlockedCopy}</p>
          <Button
            color="ghost"
            size="sm"
            className="rounded self-end"
            onClick={onClose}
            data-testid="reactivate-modal-blocked-dismiss"
          >
            {t('blocked.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- CONFIRM / PROCESSING / ERROR share the picker + summary ---- */}
      {view.status === 'confirm' ||
      view.status === 'processing' ||
      view.status === 'error' ? (
        <>
          <div className="p-4 flex flex-col gap-4">
            <p className="text-sm text-neutral-600">
              {t(
                isBeforeRenewal
                  ? 'confirm.beforeRenewal.intro'
                  : 'confirm.intro',
              )}
            </p>
            {noticeCode ? (
              <p
                className="rounded border border-info/30 bg-info/5 p-3 text-sm text-neutral-600"
                data-testid="reactivate-modal-retry-notice"
              >
                {t(
                  noticeCode === 'stale_reactivation_preview'
                    ? 'errors.stalePreview'
                    : 'errors.renewalInProgress',
                )}
              </p>
            ) : null}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-neutral-600">
                {t('confirm.yourPlan')}
              </span>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-neutral-500 transition-colors hover:text-neutral-700"
                onClick={() => setShowAllTiers((prev) => !prev)}
                disabled={isProcessing}
                data-testid="reactivate-modal-toggle"
              >
                <ChevronDownIcon
                  className={classnames('size-4 transition-transform', {
                    'rotate-180': showAllTiers,
                  })}
                  aria-hidden="true"
                />
                {showAllTiers
                  ? t('confirm.hidePlans')
                  : t('confirm.showOtherPlans')}
              </button>
            </div>

            <div
              className="flex flex-col gap-2"
              data-testid="reactivate-modal-plan-list"
            >
              {orderedTiers.map((tier) => {
                const isSelected = tier === selectedTier;
                const isSuggested = tier === suggestedTier;
                if (!isSuggested && !showAllTiers) return null;
                return (
                  <label
                    key={tier}
                    className={classnames(
                      'flex items-center gap-3 rounded border p-3 cursor-pointer',
                      {
                        'border-primary bg-primary/5': isSelected,
                        'border-base-300': !isSelected,
                      },
                    )}
                    data-testid={`reactivate-tier-option-${tier}`}
                  >
                    <input
                      type="radio"
                      name="lead-agent-reactivate-tier"
                      className="radio radio-primary radio-sm"
                      checked={isSelected}
                      onChange={() => handleSelectTier(tier)}
                      disabled={isProcessing}
                      data-testid={`reactivate-tier-radio-${tier}`}
                    />
                    <span className="flex-1 flex flex-col">
                      <span className="text-sm font-medium flex items-center gap-2">
                        {t(`tier.${tier}`)}
                        {isSuggested ? (
                          <span className="badge badge-sm badge-primary">
                            {t('confirm.recommended')}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {t(`confirm.tierDescription.${tier}`)}
                      </span>
                    </span>
                    {isSelected && selectedTierPrice ? (
                      <span
                        className="text-sm font-semibold"
                        data-testid={`reactivate-tier-price-${tier}`}
                      >
                        {selectedTierPrice}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>

            {/* ---- Charge summary (A11 preview) ---- */}
            <div
              className="rounded border border-base-300 p-3 flex flex-col gap-3"
              data-testid="reactivate-modal-summary"
            >
              {preview.status === 'loading' ? (
                <p
                  className="text-sm text-neutral-500"
                  data-testid="reactivate-modal-preview-loading"
                >
                  {t('confirm.loadingPreview')}
                </p>
              ) : null}

              {preview.status === 'error' ? (
                <div
                  className="flex flex-col gap-2"
                  data-testid="reactivate-modal-preview-error"
                >
                  <p className="text-sm text-error">{t('errors.preview')}</p>
                  <button
                    type="button"
                    className="self-start text-sm text-primary underline"
                    onClick={retry}
                    data-testid="reactivate-modal-preview-retry"
                  >
                    {t('errors.retry')}
                  </button>
                </div>
              ) : null}

              {preview.status === 'ready' && amountLabel ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="flex flex-col">
                      <span>
                        {t(
                          isSendInvoice
                            ? 'confirm.chargeInvoice'
                            : isBeforeRenewal
                              ? 'confirm.beforeRenewal.charge'
                              : 'confirm.chargeNow',
                        )}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {t(
                          isSendInvoice
                            ? 'confirm.chargeInvoiceHint'
                            : isBeforeRenewal
                              ? 'confirm.beforeRenewal.noChargeToday'
                              : 'confirm.chargeNowHint',
                        )}
                      </span>
                    </span>
                    <span
                      className="font-semibold"
                      data-testid="reactivate-modal-amount"
                    >
                      {amountLabel}
                    </span>
                  </div>
                  {isProrated ? (
                    <p
                      className="text-xs text-neutral-500"
                      data-testid="reactivate-modal-proration-note"
                    >
                      {t('confirm.prorationNote')}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>

            {/*
              ---- Next-renewal projection (A6 / RET-1287) ----
              INFORMATIONAL + fail-open: rendered only when the projection
              resolved. A loading/error projection simply omits this section;
              it never blocks the charge (RET-1417).
            */}
            {breakdown ? (
              <div
                className="rounded border border-base-300 p-3 flex flex-col gap-1"
                data-testid="reactivate-modal-renewal"
              >
                <p className="text-sm font-medium">{t('nextRenewal.title')}</p>
                {breakdown.renewalDate ? (
                  <p
                    className="text-xs text-neutral-500"
                    data-testid="reactivate-modal-renewal-date"
                  >
                    {breakdown.renewalDate}
                  </p>
                ) : null}
                {breakdown.renewalLines.map((line, index) => (
                  <div
                    key={line.isNew ? 'lead-agent-line' : `plan-line-${index}`}
                    className="flex justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      {line.isNew
                        ? t('nextRenewal.leadAgentLine', {
                            tier: t(`tier.${selectedTier}`),
                          })
                        : line.description}
                      {line.isNew ? (
                        <span className="badge badge-xs badge-primary">
                          {t('nextRenewal.new')}
                        </span>
                      ) : null}
                    </span>
                    <span>{line.amount}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold border-t border-base-200 pt-2 mt-1">
                  <span>
                    {breakdown.renewalDate
                      ? t('nextRenewal.totalDated', {
                          period: periodLabel,
                          date: breakdown.renewalDate,
                        })
                      : t('nextRenewal.total', { period: periodLabel })}
                  </span>
                  <span data-testid="reactivate-modal-renewal-total">
                    {breakdown.renewalTotal}
                  </span>
                </div>
              </div>
            ) : null}

            {/* ---- Conditional explanatory copy (informational, fail-open) ---- */}
            {copyVariant ? (
              <p
                className="text-sm text-neutral-600"
                data-testid="reactivate-modal-explainer"
              >
                {copyVariant === 'before_renewal'
                  ? breakdown?.renewalDate
                    ? t('confirm.beforeRenewal.explainerDated', {
                        date: breakdown.renewalDate,
                      })
                    : t('confirm.beforeRenewal.explainer')
                  : copyVariant === 'full'
                    ? t('confirm.explainFull')
                    : breakdown?.renewalDate
                      ? t('confirm.explainBridge', {
                          date: breakdown.renewalDate,
                        })
                      : t('confirm.explainBridgeNoDate')}
              </p>
            ) : null}

            {/* ---- Retryable error (decline / transient / unknown) ---- */}
            {view.status === 'error' ? (
              <div
                className="flex flex-col gap-1 rounded border border-error/30 bg-error/5 p-3"
                data-testid="reactivate-modal-error"
              >
                <p className="text-sm text-error">
                  {resolveDeclineCopy(view.message, tBilling) ??
                    t('errors.charge')}
                </p>
              </div>
            ) : null}

            <label
              className={classnames(
                'flex items-start gap-2 text-sm cursor-pointer',
                { 'text-error': showTermsErrorVisible },
              )}
            >
              <input
                type="checkbox"
                className={classnames('checkbox checkbox-sm mt-0.5', {
                  'checkbox-error': showTermsErrorVisible,
                })}
                checked={acceptedTerms}
                onChange={(event) => {
                  setAcceptedTerms(event.target.checked);
                  if (event.target.checked) setShowTermsError(false);
                }}
                disabled={confirmDisabled}
                data-testid="reactivate-modal-terms"
              />
              <span>
                {t(
                  isBeforeRenewal
                    ? breakdown?.renewalDate
                      ? 'confirm.beforeRenewal.termsDated'
                      : 'confirm.beforeRenewal.terms'
                    : isSendInvoice
                      ? 'confirm.termsInvoice'
                      : 'confirm.terms',
                  breakdown?.renewalDate
                    ? { date: breakdown.renewalDate }
                    : undefined,
                )}
              </span>
            </label>
            {showTermsErrorVisible ? (
              <p
                className="text-sm text-error"
                data-testid="reactivate-modal-terms-error"
              >
                {t('confirm.termsError')}
              </p>
            ) : null}
          </div>

          <div className="LeadAgentReactivateModal__Actions p-4 bg-base-200 flex gap-3">
            <Button
              color="primary"
              size="sm"
              className="rounded flex-1"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              loading={submitLoading}
              data-testid="reactivate-modal-submit"
            >
              {view.status === 'error'
                ? t('confirm.retrySubmit')
                : isBeforeRenewal
                  ? t('confirm.beforeRenewal.submit')
                  : amountLabel
                    ? t(
                        isSendInvoice
                          ? 'confirm.submitInvoiceWithAmount'
                          : 'confirm.submitWithAmount',
                        { amount: amountLabel },
                      )
                    : t(
                        isSendInvoice
                          ? 'confirm.submitInvoice'
                          : 'confirm.submit',
                      )}
            </Button>
            <Button
              color="ghost"
              size="sm"
              className="rounded"
              onClick={handleClose}
              disabled={isProcessing}
              data-testid="reactivate-modal-cancel"
            >
              {t('confirm.cancel')}
            </Button>
          </div>
        </>
      ) : null}
    </Modal.Legacy>
  );
}
